import React from 'react'
import { OmTabs, OmButton, OmDropdownMenu, OmSpinner } from '@owma/kit'
import { store as reduxStore } from 'index'
import { getTempEnvParam } from 'env'
import { resolveTimeFormats, type ResolvedTimeFormats } from 'utils'

import { cloneDeep } from 'lodash'

import SVG from 'react-inlinesvg'

import backIcon from 'assets/images/toolbar-back.svg'

import i18n from 'i18n'
import classNames from 'classnames'

import {
  confirmBase,
  // dragPopup
} from 'utils/ui'

import settingSyncService from 'services/settingSync'

import dataMgr from 'pages/main/data/DataManager'

import openSelectFolder from 'popups/SelectFolder'

import toolbarMore24 from 'assets/images/toolbar-more-24.svg'

import account24SVG from 'assets/images/settings/account-24.svg'
import general24SVG from 'assets/images/settings/general-24.svg'
import ai24SVG from 'assets/images/settings/ai-24.svg'
import email24SVG from 'assets/images/settings/email-24.svg'
import calendar24SVG from 'assets/images/settings/calendar-24.svg'
import todo24SVG from 'assets/images/settings/todo-24.svg'
import menuCheckGray16 from 'assets/images/menu-check-gray-16.svg'

import Accounts from './Accounts'
import Email from './Email'
import Calendar from './Calendar'
import General from './General'
import Todo from './Todo'
import AI from './AI'

import styles from './style.module.scss'

import { useAiSummary } from 'env'

interface SettingsOptions {
  selectedTabs?: string[]
  afterClose?: () => void
}

class Settings {
  getStore: () => any
  currentSettingsPopup: any = null
  isInit: boolean = false
  prevLocale: any = null
  unsubscribe: (() => void) | null = null
  detachEvent: (() => void) | null = null

  constructor(getStore: () => any) {
    this.getStore = getStore
  }

  init = (): void => {
    this.detachEvent = this.attachEvent()
  }

  clear = (): void => {
    if (this.detachEvent != null) {
      this.detachEvent()
      this.detachEvent = null
    }
  }

  // Settings 팝업이 열릴 때마다 init 호출하여 최신 상태로 유지하도록 함.
  initPopup = (): void => {
    if (this.isInit === false) {
      this.isInit = true

      const reduxStore = this.getStore()
      const { settings } = reduxStore.getState()
      // locale 은 local-only — localSettings.general.locale 에서 읽는다.
      this.prevLocale = settings.localSettings?.general?.locale

      this.unsubscribe = reduxStore.subscribe(this.onStoreChange)
    }
  }

  // Settings 팝업이 완전히 닫힐 때 reset 호출하여 상태 초기화.
  resetPopup = (): void => {
    this.isInit = false
    if (this.unsubscribe != null) {
      this.unsubscribe()
      this.unsubscribe = null
    }
  }

  onStoreChange = (): void => {
    const reduxState = this.getStore().getState()
    const { settings } = reduxState

    // locale 은 local-only — localSettings.general.locale 에서 읽는다.
    if (this.prevLocale !== settings.localSettings?.general?.locale) {
      this.prevLocale = settings.localSettings?.general?.locale

      this.refresh()
    }
  }

  getTimeFormat = (): ResolvedTimeFormats => {
    const reduxState = this.getStore().getState()
    const { settings } = reduxState
    return resolveTimeFormats(settings.general ?? {})
  }

  refresh = (): void => {
    if (this.currentSettingsPopup != null) {
      this.currentSettingsPopup.updateModal()
    }
  }

  getAll = (): any => {
    const reduxState = this.getStore().getState()
    const { settings } = reduxState

    const values = cloneDeep(settings)
    return values
  }

  getValue = (key: string): any => {
    const reduxState = this.getStore().getState()
    const { settings } = reduxState
    return cloneDeep(settings[key])
  }

  // locale 은 local-only 로 분리되어 localSettings.general.locale 에 저장된다.
  // (과거 general.locale 은 제거됨 — getValue('general').locale 는 항상 undefined)
  // 모든 소비처는 이 헬퍼로 locale 을 읽어 undefined 유입을 차단한다.
  getLocale = (): string => {
    const reduxState = this.getStore().getState()
    const { settings } = reduxState
    return settings.localSettings?.general?.locale ?? 'ko-KR'
  }

  setValue = (key: string, value: any): Promise<void> => {
    return this.setValues({ [key]: value })
  }

  setValues = async (originValueMap: any): Promise<void> => {
    const valueMap = cloneDeep(originValueMap)
    // dot-path 키들을 deep merge하여 카테고리 단위 트리로 구성
    const merged: Record<string, unknown> = {}

    const deepMerge = (
      target: Record<string, unknown>,
      source: Record<string, unknown>,
    ): Record<string, unknown> => {
      const result = { ...target }
      for (const [k, srcVal] of Object.entries(source)) {
        const tgtVal = result[k]
        if (
          tgtVal != null &&
          srcVal != null &&
          typeof tgtVal === 'object' &&
          typeof srcVal === 'object' &&
          !Array.isArray(tgtVal) &&
          !Array.isArray(srcVal)
        ) {
          result[k] = deepMerge(
            tgtVal as Record<string, unknown>,
            srcVal as Record<string, unknown>,
          )
        } else {
          result[k] = srcVal
        }
      }
      return result
    }

    Object.keys(valueMap).forEach((key) => {
      // dot-path를 트리 구조로 변환
      const segments = key.split('.')
      let node: Record<string, unknown> = {}
      let current = node
      for (let i = 0; i < segments.length - 1; i++) {
        current[segments[i]] = {}
        current = current[segments[i]] as Record<string, unknown>
      }
      current[segments[segments.length - 1]] = valueMap[key]

      // 기존 merged와 deep merge
      const keys = Object.keys(node)
      for (const k of keys) {
        if (
          merged[k] != null &&
          node[k] != null &&
          typeof merged[k] === 'object' &&
          typeof node[k] === 'object' &&
          !Array.isArray(merged[k]) &&
          !Array.isArray(node[k])
        ) {
          merged[k] = deepMerge(
            merged[k] as Record<string, unknown>,
            node[k] as Record<string, unknown>,
          )
        } else {
          merged[k] = node[k]
        }
      }
    })

    // general.locale은 SET_LOCALE로 별도 처리
    const general = merged.general as Record<string, unknown> | undefined
    if (general?.locale != null) {
      await new Promise<void>((resolve) => {
        this.getStore().dispatch({
          type: 'settings/SET_LOCALE',
          payload: { locale: general.locale },
          onComplete: resolve,
        })
      })

      delete general.locale
      if (Object.keys(general).length === 0) {
        delete merged.general
      }
    }

    if (Object.keys(merged).length > 0) {
      await new Promise<void>((resolve) => {
        this.getStore().dispatch({
          type: 'settings/CHANGE_SETTING',
          payload: merged,
          onComplete: resolve,
        })
      })
    }
  }

  open = (options: SettingsOptions) => {
    this.initPopup()

    const opts: SettingsOptions = {
      ...options,
      afterClose: () => {
        if (options != null && options.afterClose != null) {
          options.afterClose()
        }
        this.currentSettingsPopup = null
        this.resetPopup()
      },
    }

    this.currentSettingsPopup = openSettings(opts)
  }

  // 서버 설정 변경 → Redux 반영 리스너
  // settingSyncService.notifyListeners가 콜백 실행 동안 isApplyingServerSettings()=true를
  // 자동 보장하므로 별도 플래그 관리가 불필요. 콜백 내부에서 발생하는 setValue → CHANGE_SETTING →
  // (subscriber로 인한) 후속 UPLOAD_SETTINGS는 saga에서 자동 차단된다.
  attachEvent = (): (() => void) => {
    return settingSyncService.addChangeListener(async (serverSetting) => {
      for (const [key, value] of Object.entries(serverSetting)) {
        if (key === 'calendar' && value && typeof value === 'object') {
          const currentCalendar = this.getValue('calendar') || {}
          if (JSON.stringify(currentCalendar) !== JSON.stringify(value)) {
            await this.setValue('calendar', { ...currentCalendar, ...value })
            if (this.currentSettingsPopup != null) {
              this.currentSettingsPopup.reactCompMap[key]?.current?.applySettings()
            }
          }
        } else {
          const currentValue = this.getValue(key)
          if (JSON.stringify(currentValue) !== JSON.stringify(value)) {
            await this.setValue(key, value)
            if (this.currentSettingsPopup != null) {
              this.currentSettingsPopup.reactCompMap[key]?.current?.applySettings()
            }
          }
        }
      }
    })
  }
}

const settings = new Settings(() => {
  return reduxStore
})

function equalsProp(x: any, y: any): boolean {
  if (x === y) {
    return true
  }

  if (Array.isArray(x) && Array.isArray(y)) {
    return x.length === y.length && x.every((value, index) => equalsProp(value, y[index]))
  } else if (Array.isArray(x) !== Array.isArray(y)) {
    return false
  }

  if (!(x instanceof Object) || !(y instanceof Object)) {
    return false
  }

  const xArray = Object.keys(x)
  const yArray = Object.keys(y)

  if (xArray.length !== yArray.length) {
    return false
  }

  const ret = xArray.some((key) => {
    return equalsProp(x[key], y[key]) === false
  })

  return ret === false
}

const deepGet = (obj: Record<string, unknown>, path: string): unknown => {
  const keys = path.split('.')
  let current: unknown = obj
  for (const k of keys) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[k]
  }
  return current
}

const deepSet = (obj: Record<string, unknown>, path: string, value: unknown): void => {
  const keys = path.split('.')
  let current: Record<string, unknown> = obj
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    if (current[k] == null || typeof current[k] !== 'object') {
      current[k] = {}
    }
    current = current[k] as Record<string, unknown>
  }
  current[keys[keys.length - 1]] = value
}

const deepDelete = (obj: Record<string, unknown>, path: string): void => {
  const keys = path.split('.')
  if (keys.length === 1) {
    delete obj[keys[0]]
    return
  }
  // 경로 상의 부모 객체를 스택에 기록
  const parents: { obj: Record<string, unknown>; key: string }[] = []
  let current: Record<string, unknown> = obj
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    if (current[k] == null || typeof current[k] !== 'object') return
    parents.push({ obj: current, key: k })
    current = current[k] as Record<string, unknown>
  }
  delete current[keys[keys.length - 1]]

  // 빈 부모 객체를 역순으로 정리
  for (let i = parents.length - 1; i >= 0; i--) {
    const { obj: parent, key } = parents[i]
    const child = parent[key] as Record<string, unknown>
    if (Object.keys(child).length === 0) {
      delete parent[key]
    } else {
      break
    }
  }
}

function openSettings(options: SettingsOptions): any {
  const opts = options || {}

  const selectedTabs = opts.selectedTabs || []

  const defaultActiveKey = selectedTabs.length > 0 ? selectedTabs[0] : null

  const defaultValues = settings.getAll()

  const tmpObj: any = {
    selectedTabs,
    loading: true,
    saveDisabled: true,
    defaultValues: cloneDeep(defaultValues),
    reactCompMap: {
      account: React.createRef(),
      general: React.createRef(),
      email: React.createRef(),
      calendar: React.createRef(),
      todo: React.createRef(),
      ai: React.createRef(),
    },
    newValues: {},
  }

  settingSyncService
    .sync()
    .catch(() => {})
    .finally(() => {
      const allValues = settings.getAll()
      tmpObj.defaultValues = cloneDeep(allValues)
      tmpObj.setLoading(false)
    })

  tmpObj.close = () => {
    tmpObj.modalObj.destroy()
  }

  tmpObj.onChangeValue = (key: string, value: any) => {
    const oldValue = deepGet(tmpObj.defaultValues, key)
    const isValueChanged = equalsProp(oldValue, value) !== true
    if (isValueChanged === true) {
      deepSet(tmpObj.newValues, key, value)
    } else {
      deepDelete(tmpObj.newValues, key)
    }

    const hasChanges = Object.keys(tmpObj.newValues).length > 0
    tmpObj.saveDisabled = !hasChanges
    tmpObj.updateModal()
  }

  const viewObj: any = {
    info: {
      depth: 0,
      list: null,
      idx: 0,
    },
    setKey: (depth: number, key: string, labels: any[], idx: number) => {
      const { info } = viewObj
      if (depth > 0) {
        info.list = labels
        info.depth = depth
        info.idx = idx

        console.log(' --- setKey : ', depth, key, labels)
        tmpObj.updateModal()
      }
    },
    setDepth: (depth: number) => {
      const { info } = viewObj
      info.depth = depth
      tmpObj.updateModal()
    },
  }

  tmpObj.mobileViewObj = viewObj

  tmpObj.getTitle = () => {
    const { info } = viewObj
    const { user, appsettings } = reduxStore.getState()

    if (info.depth > 0 && appsettings.viewMode === 'mobile') {
      const label = info.list[0]
      let newTitle = label
      if (info.list.length > 1) {
        const nextLabel = info.list[1]
        newTitle = [label, <span key="sep"> / </span>, nextLabel]
      }

      return (
        <div className={styles.title}>
          <OmButton
            variant="text"
            icon={<img src={backIcon} alt="Back" />}
            className={classNames(styles.backButton, 'backButton')}
            onClick={() => {
              viewObj.setDepth(info.depth - 1)
            }}
          />
          {newTitle}
        </div>
      )
    }

    return (
      <div className={styles.title}>
        <OmButton
          variant="text"
          icon={<img src={backIcon} alt="Back" />}
          className={classNames(styles.backButton, 'backButton')}
          onClick={tmpObj.close}
        />
        {i18n.t('settings.title.settings')}
        <span className={styles.email}>{user.email}</span>
      </div>
    )
  }

  tmpObj.renderAccounts = (attrs: any) => {
    return <Accounts owner={tmpObj} innerRef={tmpObj.reactCompMap.account} {...attrs} />
  }

  tmpObj.renderEmail = (attrs: any) => {
    return <Email owner={tmpObj} innerRef={tmpObj.reactCompMap.email} {...attrs} />
  }

  tmpObj.renderCalendar = (attrs: any) => {
    return <Calendar owner={tmpObj} innerRef={tmpObj.reactCompMap.calendar} {...attrs} />
  }

  tmpObj.renderGeneral = (attrs: any) => {
    return <General owner={tmpObj} innerRef={tmpObj.reactCompMap.general} {...attrs} />
  }

  tmpObj.renderTodo = (attrs: any) => {
    return <Todo owner={tmpObj} innerRef={tmpObj.reactCompMap.todo} {...attrs} />
  }

  tmpObj.renderAI = (attrs: any) => {
    return <AI owner={tmpObj} innerRef={tmpObj.reactCompMap.ai} {...attrs} />
  }

  tmpObj.getTabsAttrs = (attrs?: any) => {
    const { t } = i18n
    console.log(' ----- getTabsAttrs : ', i18n, i18n.language, i18n.t('settings.tabs.accounts'))

    const { appsettings } = reduxStore.getState()

    // app.services 는 로그인/랜딩 흐름에서 설정되고 로그아웃 정리 시 제거된다. 미설정 세션 상태
    // (로그인 미완료 또는 강제 로그아웃 직후 등)에서 설정을 열면 services.pages 접근이 크래시하므로,
    // pages 가 항상 객체가 되도록 방어한다. 미확정 시 각 탭 조건은 false 로 평가되어 해당 탭이 숨겨진다.
    const rawServices = getTempEnvParam('app.services')
    const services = {
      ...(rawServices ?? {}),
      pages: rawServices?.pages ?? {},
    }

    // OFM-186: OfficeChat 등 임베디드 환경(UA: OfficeChat/Electron/popup-calendar)에서
    // calendar/todo 로 진입한 경우, 해당 앱의 설정만 노출한다.
    const embeddedAppFilter: 'calendar' | 'todo' | null = (() => {
      if (appsettings.isOfficenextMessenger !== true) return null
      const pathname = window.location.pathname
      if (pathname.startsWith('/calendar')) return 'calendar'
      if (pathname.startsWith('/todo')) return 'todo'
      return null
    })()

    const accountLabel = (
      <React.Fragment>
        <div className={classNames(styles.tabItem, styles.accounts)}>
          <div className={classNames(styles.icon)}>
            <SVG src={account24SVG} title="accounts" />
          </div>
          <div className={classNames(styles.label)}>{t('settings.tabs.accounts')}</div>
        </div>
      </React.Fragment>
    )

    const generalLabel = (
      <React.Fragment>
        <div className={classNames(styles.tabItem, styles.general)}>
          <div className={classNames(styles.icon)}>
            <SVG src={general24SVG} title="general" />
          </div>
          <div className={classNames(styles.label)}>{t('settings.tabs.general')}</div>
        </div>
      </React.Fragment>
    )

    let idx = 0

    const items: any[] = []

    // !(!email && calendar)
    if (
      services.pages.email === true &&
      embeddedAppFilter === null
      // || (services.pages.calendar === false && services.pages.todo === false)
    ) {
      items.push({
        key: 'accounts',
        label: accountLabel,
        children: tmpObj.renderAccounts({
          ...attrs,
          isMobile: appsettings.viewMode === 'mobile',
          parentLabel: accountLabel,
          idx: idx++,
        }),
        forceRender: appsettings.viewMode === 'mobile',
      })
    }

    items.push({
      key: 'general',
      label: generalLabel,
      children: tmpObj.renderGeneral({
        ...attrs,
        isMobile: appsettings.viewMode === 'mobile',
        parentLabel: generalLabel,
        idx: idx++,
      }),
      forceRender: appsettings.viewMode === 'mobile',
    })

    if (services.pages.email === true && embeddedAppFilter === null) {
      const emailLabel = (
        <React.Fragment>
          <div className={classNames(styles.tabItem, styles.email)}>
            <div className={classNames(styles.icon)}>
              <SVG src={email24SVG} title="email" />
            </div>
            <div className={classNames(styles.label)}>{t('settings.tabs.email')}</div>
          </div>
        </React.Fragment>
      )
      items.push({
        key: 'email',
        label: emailLabel,
        children: tmpObj.renderEmail({
          ...attrs,
          isMobile: appsettings.viewMode === 'mobile',
          parentLabel: emailLabel,
          idx: idx++,
        }),
        forceRender: appsettings.viewMode === 'mobile',
      })
    }

    if (
      services.pages.calendar === true &&
      (embeddedAppFilter === null || embeddedAppFilter === 'calendar')
    ) {
      const calendarLabel = (
        <React.Fragment>
          <div className={classNames(styles.tabItem, styles.calendar)}>
            <div className={classNames(styles.icon)}>
              <SVG src={calendar24SVG} title="calendar" />
            </div>
            <div className={classNames(styles.label)}>{t('settings.tabs.calendar')}</div>
          </div>
        </React.Fragment>
      )
      items.push({
        key: 'calendar',
        label: calendarLabel,
        children: tmpObj.renderCalendar({
          ...attrs,
          isMobile: appsettings.viewMode === 'mobile',
          parentLabel: calendarLabel,
          idx: idx++,
        }),
        forceRender: appsettings.viewMode === 'mobile',
      })
    }

    if (
      services.pages.todo === true &&
      (embeddedAppFilter === null || embeddedAppFilter === 'todo')
    ) {
      const todoLabel = (
        <React.Fragment>
          <div className={classNames(styles.tabItem, styles.todo)}>
            <div className={classNames(styles.icon)}>
              <SVG src={todo24SVG} title="todo" />
            </div>
            <div className={classNames(styles.label)}>{t('settings.tabs.todo')}</div>
          </div>
        </React.Fragment>
      )
      items.push({
        key: 'todo',
        label: todoLabel,
        children: tmpObj.renderTodo({
          ...attrs,
          isMobile: appsettings.viewMode === 'mobile',
          parentLabel: todoLabel,
          idx: idx++,
        }),
        forceRender: appsettings.viewMode === 'mobile',
      })
    }

    if (useAiSummary(true) === true && embeddedAppFilter === null) {
      const aiLabel = (
        <React.Fragment>
          <div className={classNames(styles.tabItem, styles.ai)}>
            <div className={classNames(styles.icon)}>
              <SVG src={ai24SVG} title="AI" />
            </div>
            <div className={classNames(styles.label)}>{t('settings.tabs.ai')}</div>
          </div>
        </React.Fragment>
      )
      items.push({
        key: 'ai',
        label: aiLabel,
        children: tmpObj.renderAI({
          ...attrs,
          isMobile: appsettings.viewMode === 'mobile',
          parentLabel: aiLabel,
          idx: idx++,
        }),
        forceRender: appsettings.viewMode === 'mobile',
      })
    }

    const tabsAttrs: any = {
      className: classNames('iconTabs'),
      items,
      centered: true,
      tabPosition: 'left',
      destroyOnHidden: true,
      // onChange: (activeKey) => {
      //   const item = items.find((item) => item.key === activeKey);
      //   if (item != null) {
      //     tmpObj.mobileViewObj.setKey(1, activeKey, item.label);
      //   }
      // },
      // onTabClick: (activeKey) => {
      //   const item = items.find((item) => item.key === activeKey)
      //   if (item != null) {
      //     tmpObj.mobileViewObj.setKey(1, activeKey, item.label)
      //   }
      // },
    }

    if (defaultActiveKey != null) {
      tabsAttrs.defaultActiveKey = defaultActiveKey
    }

    return tabsAttrs
  }

  tmpObj.render = () => {
    const tabsAttrs = tmpObj.getTabsAttrs()

    return (
      <div
        className={classNames(styles.settings)}
        data-depth={tmpObj.mobileViewObj.info.depth}
        data-idx={tmpObj.mobileViewObj.info.idx}
      >
        <OmSpinner spinning={tmpObj.loading}>
          <OmTabs {...tabsAttrs} />
        </OmSpinner>
      </div>
    )
  }

  tmpObj.setLoading = (value: boolean) => {
    tmpObj.loading = value
    tmpObj.updateModal()
  }

  tmpObj.modalObj = confirmBase({
    // wrapClassName: classNames('noPadding', 'noBtn', styles.settingsModal),
    wrapClassName: classNames('noPadding', styles.settingsModal, 'settingsModal'),
    className: classNames('mobile-fullpage'),
    title: tmpObj.getTitle(),
    content: tmpObj.render(),
    width: 800,
    // footer: null,
    okText: i18n.t('common.button.save'),
    cancelText: i18n.t('common.button.cancel'),
    okButtonProps: { disabled: tmpObj.saveDisabled },
    onOk: async () => {
      await settings.setValues(tmpObj.newValues)

      // 변경된 settings를 서버에 동기화. localSettings로 시작하는 키는 서버 동기화 대상에서 제외.
      // 서버→로컬 적용 중인 경우(addChangeListener 콜백 실행 중 + 종료 후 3초)에는 UPLOAD_SETTINGS
      // saga가 settingSyncService.isApplyingServerSettings() 체크로 자체 차단하므로 onOk에서
      // 별도 가드는 불필요. (사용자가 직접 OK를 누른 경로에서는 항상 false이므로 push가 정상 진행됨)
      const syncEntries: Record<string, unknown> = {}
      Object.entries(tmpObj.newValues).forEach(([key, value]) => {
        if (key.startsWith('localSettings')) return
        syncEntries[key] = value
      })
      if (Object.keys(syncEntries).length > 0) {
        settings.getStore().dispatch({
          type: 'settings/UPLOAD_SETTINGS',
          payload: syncEntries,
        })
      }

      // 초기화 및 refresh가 필요한 경우.
      if (
        tmpObj.newValues.email != null &&
        (tmpObj.newValues.email.mailUseThread != null ||
          tmpObj.newValues.email.visibleThreadItems != null)
      ) {
        dataMgr.init()
        dataMgr.firstLoad(() => {
          dataMgr.reactMgr.updateUI()
        })
        // history.replace('/email', { state: { reload: true, test: true } })
        // history.push(0)
        // history.pushState({ reload: true, test: true }, null)
      } else {
        // refresh가 필요한 경우.
        if (tmpObj.newValues.ai != null) {
          const prevAi = settings.getValue('ai')

          if (prevAi == null || prevAi.summary !== tmpObj.newValues.ai.summary) {
            tmpObj._updateUITimer = setTimeout(() => {
              dataMgr.reactMgr.updateUI()
            }, 100)
          }
        }
      }
    },
    afterClose: () => {
      if (tmpObj._updateUITimer != null) {
        clearTimeout(tmpObj._updateUITimer)
      }
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  })

  // tmpObj.dragPopup = dragPopup(tmpObj);

  tmpObj.updateModal = () => {
    tmpObj.modalObj.updateBase({
      title: tmpObj.getTitle(),
      content: tmpObj.render(),
      okButtonProps: { disabled: tmpObj.saveDisabled },
    })
  }

  return tmpObj
}

export function getCustomFolderSelect(role: string, nameKey: string): any {
  const customInfo: any = {
    role,
  }

  const customSystemFolderSelect = {
    onGetValue: (/* info, wrapper */): void => {
      // console.log(wrapper, info)
    },
    onSetValue: (/* value, info, wrapper */): void => {
      // console.log(' --- onSetValue : ', wrapper, info, value)
      // customInfo.value = value
      // const { reactObj } = wrapper
      // reactObj.forceUpdate()
    },
    getElement: (info: any, wrapper: any): React.ReactElement => {
      // console.log(wrapper, info, reactObj)
      const { reactObj } = wrapper
      const { owner } = reactObj.props

      const clearValue = (): void => {
        let folder = dataMgr.getMailboxForRole(customInfo.role)
        if (folder != null) {
          const ret = folder.setRole('')
          if (ret != null) {
            owner.setLoading(true)
            ret.then(() => {
              owner.setLoading(false)
              const { reactObj } = wrapper
              reactObj.forceUpdate()
            })
          }
        }
      }

      const setFolder = (): void => {
        const root = dataMgr.getRootMailbox()
        const except = [root]

        // root 폴더는 선택 불가.
        openSelectFolder({
          except,
          title: i18n.t('settings.subtitle.setArchiveFolder'),
          okText: i18n.t('common.button.ok'),
          onSelect: (key: any) => {
            const selectedFolder = dataMgr.getMailboxById(key)
            const ret = selectedFolder.setRole(customInfo.role)
            if (ret != null) {
              owner.setLoading(true)
              ret.then(() => {
                owner.setLoading(false)
                console.log(' --- setFolder : ', key, customInfo.role, selectedFolder)
                reactObj.forceUpdate()
              })
            }
          },
        })
      }

      const onClickText = (e: React.MouseEvent): void => {
        console.log(' --- onClickText : ', e)
        if (e.target != null) {
          const target = e.target as HTMLElement
          const afterComponent = target.parentElement?.querySelector('.after-component')
          if (afterComponent != null) {
            ;(afterComponent as HTMLElement).focus()
          }
        }
      }

      let menuBtn = null
      let folderName = i18n.t('settings.folder.unset')

      let folder = dataMgr.getMailboxForRole(customInfo.role)

      console.log(' --- system folder : ', customInfo.role, folder)

      if (folder != null) {
        folderName = folder.getName()

        const menuAttrs = {
          items: [
            {
              label: i18n.t('settings.folder.clear'),
              key: 'clear',
            },
            {
              /* i18next-extract-disable-next-line */
              label: i18n.t('settings.folder.set', { name: i18n.t(nameKey) }),
              key: 'set',
            },
          ],
          onClick: ({ key }: { key: string }) => {
            switch (key) {
              case 'clear':
                clearValue()
                break
              case 'set':
                setFolder()
                break
              default:
            }
          },
        }

        menuBtn = (
          <OmDropdownMenu
            overlayClassName={classNames('contextMenu')}
            menu={menuAttrs}
            trigger={['click']}
          >
            <OmButton
              className={classNames('after-component')}
              variant="text"
              icon={<img src={toolbarMore24} alt="more" />}
            />
          </OmDropdownMenu>
        )
      } else {
        menuBtn = (
          <OmButton
            className={classNames('after-component')}
            variant="text"
            icon={<img src={toolbarMore24} alt="more" />}
            onClick={setFolder}
          />
        )
      }

      // const inputAttrs = {
      //   ...info.inputAttrs,
      //   defaultValue: customInfo.value,
      //   ref: customInfo.ref,
      //   addonAfter: menuBtn,
      // }

      // return <Input {...inputAttrs} />

      return (
        <div className={classNames('setting-component')} onClick={onClickText}>
          <div className={classNames('setting-value')}>{folderName}</div>
          {menuBtn}
        </div>
      )
    },
  }

  return customSystemFolderSelect
}

export function getCustomSignatureSelect(defaultSignatureId?: string): any {
  const customInfo: any = {
    value: defaultSignatureId,
  }

  const customSystemFolderSelect = {
    onGetValue: (info: any, wrapper: any): void => {
      console.log('-- getCustomSignatureSelect - onGetValue : ', info, wrapper, customInfo.value)
      wrapper.value = customInfo.value
    },
    onSetValue: (value: any, info: any, wrapper: any): void => {
      console.log('-- getCustomSignatureSelect - onSetValue : ', value, info, wrapper)
      customInfo.value = value
      const { reactObj } = wrapper
      reactObj.forceUpdate()
    },
    getElement: (info: any, wrapper: any): React.ReactElement => {
      console.log('-- getCustomSignatureSelect - getElement : ', info, wrapper)

      const { reactObj } = wrapper
      // const { owner } = reactObj.props

      const signatureList = dataMgr.getSignatureList()

      const items = signatureList.map((signature: any) => {
        const { title, titleKey, signatureId } = signature
        return {
          /* i18next-extract-disable-next-line */
          label: titleKey != null ? i18n.t(titleKey) : title,
          key: signatureId,
        }
      })

      const menuAttrs = {
        items: [
          {
            label: i18n.t('editor.signature.none'),
            key: 'none',
          },
          ...items,
        ],
        onClick: ({ key }: { key: string }) => {
          console.log(' --- onMenuClick : ', key)
          reactObj.onPropsChange(wrapper.pKey, key)
          wrapper.setValue(key)
        },
      }

      const menuBtn = (
        <OmDropdownMenu
          overlayClassName={classNames('contextMenu')}
          menu={menuAttrs}
          trigger={['click']}
        >
          <OmButton
            className={classNames('after-component')}
            variant="text"
            icon={<img src={toolbarMore24} alt="more" />}
          />
        </OmDropdownMenu>
      )

      let currentSignature = null

      if (items.length > 0) {
        currentSignature = items.find((item: any) => item.key === customInfo.value)
      }

      const currentSignatureLabel =
        currentSignature != null ? currentSignature.label : i18n.t('editor.signature.none')

      return (
        <div className={classNames('setting-component')}>
          <div className={classNames('setting-value')}>{currentSignatureLabel}</div>
          {menuBtn}
        </div>
      )
    },
  }

  return customSystemFolderSelect
}

export function getCustomSendAsSelect(items: any): any {
  const customInfo: any = {
    value: items,
  }

  const customSendAsSelect = {
    onGetValue: (info: any, wrapper: any): void => {
      console.log('-- getCustomSendAsSelect - onGetValue : ', info, wrapper)
    },
    onSetValue: (value: any, info: any, wrapper: any): void => {
      console.log('-- getCustomSendAsSelect - onSetValue : ', value, info, wrapper)
      customInfo.value = value

      const { reactObj } = wrapper
      reactObj.forceUpdate()
    },
    getElement: (info: any, wrapper: any): React.ReactElement => {
      console.log('-- getCustomSendAsSelect - getElement : ', info, wrapper)

      const { reactObj } = wrapper
      // const { owner } = reactObj.props

      const aliasList = reactObj.state.sendAsAccounts

      const items = aliasList.map((alias: any) => {
        const { name, email } = alias
        const isSelected = Object.values(customInfo.value).some((item: any) => item.email === email)
        return {
          /* i18next-extract-disable-next-line */
          label: `${name} <${email}>`,
          key: email,
          extra: isSelected ? <img src={menuCheckGray16} alt="check" /> : null,
        }
      })

      const menuAttrs = {
        items: [...items],
        onClick: (item: any) => {
          console.log(' --- onMenuClick : ', item)

          const newCustomInfo = { ...customInfo.value }
          const index = Object.values(newCustomInfo).findIndex((v: any) => v.email === item.key)
          if (index === -1) {
            const aliasList = reactObj.state.sendAsAccounts
            const alias = aliasList.find((a: any) => a.email === item.key)
            if (alias != null) {
              newCustomInfo[alias.email] = alias
            }
          } else {
            delete newCustomInfo[item.key]
          }

          reactObj.onPropsChange(wrapper.pKey, newCustomInfo)
          wrapper.setValue(newCustomInfo)
        },
      }

      const menuBtn = (
        <OmDropdownMenu
          overlayClassName={classNames('contextMenu')}
          menu={menuAttrs}
          trigger={['click']}
          destroyOnHidden={true}
        >
          <OmButton
            className={classNames('after-component')}
            variant="text"
            icon={<img src={toolbarMore24} alt="more" />}
          />
        </OmDropdownMenu>
      )

      let label = Object.values(customInfo.value).reduce((acc: string, item: any) => {
        if (acc.length > 0) {
          acc += ', '
        }
        return acc + `${item.name} <${item.email}>`
      }, '')

      if (label.length === 0) {
        label = i18n.t('settings.accountTabs.none')
      }

      return (
        <div className={classNames('setting-component')}>
          <div className={classNames('setting-value', 'appended-component')}>{label}</div>
          {menuBtn}
        </div>
      )
    },
  }

  return customSendAsSelect
}

export default settings
